import { useCallback, useRef, useState } from 'react';
import { openHeadlessOAuthWindow } from './openHeadlessOAuthWindow';

export interface HeadlessOAuthLauncher {
  /**
   * Opens the OAuth popup. MUST be called synchronously from the user gesture
   * (button onClick / openModal) — otherwise the browser blocks the popup.
   * No-op when `mode !== 'headless'`.
   */
  openOAuthPopup: () => void;
  /** The popup opened on the last gesture, to hand to the headless flow. */
  oAuthWindow: Window | null;
  /**
   * Increments on each launch. Use as a React `key` on the rendered flow so
   * every attempt mounts a fresh instance that drives the popup it just opened
   * — independent of whether the previous attempt's outcome was detectable.
   */
  flowKey: number;
}

/**
 * Single owner of the headless gesture→popup→restart concern, shared by every
 * trigger (button / useModal / useUnifiedAccount) so the logic lives in one
 * place instead of being copied per trigger.
 */
export function useHeadlessOAuthLauncher(
  mode: 'modal' | 'panel' | 'headless' | undefined,
  appName: string | undefined
): HeadlessOAuthLauncher {
  const windowRef = useRef<Window | null>(null);
  const [flowKey, setFlowKey] = useState(0);

  const openOAuthPopup = useCallback(() => {
    if (mode !== 'headless') {
      return;
    }
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.close();
    }
    windowRef.current = openHeadlessOAuthWindow(appName);
    setFlowKey((n) => n + 1);
  }, [mode, appName]);

  // oAuthWindow is a ref read at render time. openOAuthPopup both writes the ref
  // and calls setFlowKey, which schedules a re-render. By the time that render
  // runs, windowRef.current holds the newly opened window, so the snapshot is
  // always fresh when consumers actually read it (React batches the state update
  // with whatever other state change triggered the re-render).
  // eslint-disable-next-line react-hooks/refs
  return { openOAuthPopup, oAuthWindow: windowRef.current, flowKey };
}
